import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  ArrowLeft,
  Plus,
  Edit,
  Trash2,
  Music2,
  ExternalLink,
  Eye,
  EyeOff,
} from 'lucide-react'
import { useMusicRelease, useMusicTracks, useDeleteTrack, useUpdateRelease } from '@/hooks/use-music'
import type { Tables } from '@/types/supabase'
import ReleaseFormModal from '@/components/music/release-form-modal'
import TrackFormModal from '@/components/music/track-form-modal'

type MusicTrack = Tables<'music_tracks'>

const STREAMING_PLATFORMS: { key: string; label: string }[] = [
  { key: 'spotify', label: 'Spotify' },
  { key: 'apple_music', label: 'Apple Music' },
  { key: 'youtube', label: 'YouTube' },
  { key: 'youtube_music', label: 'YouTube Music' },
]

const RELEASE_TYPE_LABELS: Record<string, string> = {
  single: 'Single',
  album: 'Album',
  ep: 'EP',
}

function secondsToDuration(secs: number | null | undefined): string {
  if (secs == null) return ''
  const m = Math.floor(secs / 60)
  const s = secs % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

export default function ReleaseDetailPage() {
  const { releaseId } = useParams<{ releaseId: string }>()
  const { data: release, isLoading: loadingRelease } = useMusicRelease(releaseId)
  const { data: tracks = [], isLoading: loadingTracks } = useMusicTracks(releaseId)
  const deleteTrack = useDeleteTrack()
  const updateRelease = useUpdateRelease()

  const [releaseModalOpen, setReleaseModalOpen] = useState(false)
  const [trackModalOpen, setTrackModalOpen] = useState(false)
  const [editingTrack, setEditingTrack] = useState<MusicTrack | undefined>(undefined)

  function openEditTrack(track: MusicTrack) {
    setEditingTrack(track)
    setTrackModalOpen(true)
  }

  function openAddTrack() {
    setEditingTrack(undefined)
    setTrackModalOpen(true)
  }

  function closeTrackModal() {
    setTrackModalOpen(false)
    setEditingTrack(undefined)
  }

  if (loadingRelease) return <p className="text-muted-foreground">Memuat release...</p>
  if (!release) return <p className="text-muted-foreground">Release tidak ditemukan.</p>

  const streamingLinks = (release.streaming_links as Record<string, string> | null) ?? {}
  const activeStreamingLinks = STREAMING_PLATFORMS.filter((p) => streamingLinks[p.key])

  return (
    <div className="space-y-6">
      {/* Back + header */}
      <div className="flex items-start gap-4">
        <Link
          to="/music"
          className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Kembali
        </Link>
      </div>

      {/* Release header */}
      <div className="flex gap-6 items-start">
        {/* Cover */}
        <div className="shrink-0 w-32 h-32 rounded-xl overflow-hidden bg-muted">
          {release.cover_url ? (
            <img src={release.cover_url} alt={release.title} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <Music2 className="h-10 w-10 text-muted-foreground/30" />
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
              {RELEASE_TYPE_LABELS[release.release_type] ?? release.release_type}
            </span>
            {release.release_date && (
              <span className="text-sm text-muted-foreground">{release.release_date}</span>
            )}
            <span
              className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                release.is_published
                  ? 'bg-green-100 text-green-700'
                  : 'bg-yellow-100 text-yellow-700'
              }`}
            >
              {release.is_published ? 'Publik' : 'Draft'}
            </span>
          </div>

          <h1 className="text-2xl font-bold">{release.title}</h1>

          {release.description && (
            <p className="text-sm text-muted-foreground">{release.description}</p>
          )}

          {/* Streaming links */}
          {activeStreamingLinks.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-1">
              {activeStreamingLinks.map((p) => (
                <a
                  key={p.key}
                  href={streamingLinks[p.key]}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1 text-xs hover:bg-accent transition-colors"
                >
                  <ExternalLink className="h-3 w-3" />
                  {p.label}
                </a>
              ))}
            </div>
          )}

          {/* Edit actions */}
          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={() => setReleaseModalOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-accent transition-colors"
            >
              <Edit className="h-3.5 w-3.5" />
              Edit Release
            </button>
            <button
              onClick={() =>
                updateRelease.mutate({ id: release.id, is_published: !release.is_published })
              }
              className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-accent transition-colors"
            >
              {release.is_published ? (
                <><EyeOff className="h-3.5 w-3.5" /> Sembunyikan</>
              ) : (
                <><Eye className="h-3.5 w-3.5" /> Publikasikan</>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Track list */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Daftar Lagu</h2>
          <button
            onClick={openAddTrack}
            className="flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" />
            Tambah Lagu
          </button>
        </div>

        {loadingTracks ? (
          <p className="text-muted-foreground text-sm">Memuat lagu...</p>
        ) : tracks.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border py-10 text-center">
            <Music2 className="mx-auto h-8 w-8 text-muted-foreground/40 mb-2" />
            <p className="text-sm text-muted-foreground">Belum ada lagu. Tambahkan lagu pertama.</p>
            <button
              onClick={openAddTrack}
              className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              <Plus className="h-4 w-4" />
              Tambah Lagu
            </button>
          </div>
        ) : (
          <div className="rounded-xl border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium w-12">#</th>
                  <th className="px-4 py-3 text-left font-medium">Judul</th>
                  <th className="px-4 py-3 text-left font-medium">Durasi</th>
                  <th className="px-4 py-3 text-left font-medium">Lirik</th>
                  <th className="px-4 py-3 text-right font-medium">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {tracks.map((track) => (
                  <tr key={track.id} className="bg-card hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3 text-muted-foreground">{track.track_number}</td>
                    <td className="px-4 py-3 font-medium">{track.title}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {secondsToDuration(track.duration_seconds) || '—'}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {track.lyrics ? (
                        <span className="rounded-full bg-muted px-2 py-0.5 text-xs">Ada</span>
                      ) : (
                        <span className="text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEditTrack(track)}
                          className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                          title="Edit lagu"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            if (confirm(`Hapus lagu "${track.title}"?`))
                              deleteTrack.mutate({ id: track.id, releaseId: releaseId! })
                          }}
                          className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                          title="Hapus lagu"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      <ReleaseFormModal
        open={releaseModalOpen}
        onClose={() => setReleaseModalOpen(false)}
        release={release}
      />

      <TrackFormModal
        open={trackModalOpen}
        onClose={closeTrackModal}
        releaseId={releaseId!}
        track={editingTrack}
        nextTrackNumber={tracks.length + 1}
      />
    </div>
  )
}
